import React, { useMemo } from "react";

type PieDatum = {
  label: string;
  value: number;
  color: string;
};

type Props = {
  data: PieDatum[];
  width?: number;
  height?: number;
  innerRadius?: number;
  showLabels?: boolean;
  legend?: boolean;
};

/**
 * Lightweight SVG PieChart (no external chart libs).
 */
export default function PieChart({
  data,
  width = 240,
  height = 240,
  innerRadius = 60,
  showLabels = true,
  legend = false,
}: Props) {
  const cx = width / 2;
  const cy = height / 2;
  const r = Math.min(cx, cy) - 6;

  const total = data.reduce((s, d) => s + (Number.isFinite(d.value) ? d.value : 0), 0) || 1;

  const arcs = useMemo(() => {
    let start = -Math.PI / 2;
    return data.map((d) => {
      const value = Number.isFinite(d.value) ? d.value : 0;
      const angle = (value / total) * Math.PI * 2;
      const end = start + angle;

      const x1 = cx + r * Math.cos(start);
      const y1 = cy + r * Math.sin(start);
      const x2 = cx + r * Math.cos(end);
      const y2 = cy + r * Math.sin(end);
      const large = angle > Math.PI ? 1 : 0;

      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      const mid = start + angle / 2;
      const lx = cx + (r + 14) * Math.cos(mid);
      const ly = cy + (r + 14) * Math.sin(mid);

      start = end;
      return { ...d, path, lx, ly, pct: Math.round((value / total) * 100) };
    });
  }, [cx, cy, data, r, total]);

  return (
    <div className="flex flex-col items-center gap-3">
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label="Pie chart">
        {arcs.map((a) => (
          <path key={a.label} d={a.path} fill={a.color} opacity={a.value > 0 ? 1 : 0.35}>
            <title>
              {a.label}: {a.value} ({a.pct}%)
            </title>
          </path>
        ))}

        {/* donut hole */}
        <circle cx={cx} cy={cy} r={Math.max(0, innerRadius)} fill="var(--background)" />

        {showLabels &&
          arcs
            .filter((a) => a.value > 0)
            .map((a) => (
              <text
                key={`${a.label}-label`}
                x={a.lx}
                y={a.ly}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10"
                fill="currentColor"
                opacity="0.85"
              >
                {a.pct}%
              </text>
            ))}
      </svg>

      {legend ? (
        <div className="w-full space-y-1">
          {data.map((d) => (
            <div key={d.label} className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                <span>{d.label}</span>
              </div>
              <span className="font-medium text-foreground">{d.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}


